import React, { useEffect, useMemo, useState } from 'react';
import { CoverTile, STATUS_META, effectivePracticePictures, gameReadiness } from './activityAdminShared';
import PicturesSection from './PicturesSection';

const LEGACY_FIELDS = [
  { key: 'prompts', label: 'Prompts', multiline: true },
  { key: 'level', label: 'Level (number, superseded by level tags)', type: 'number' },
  { key: 'search_terms', label: 'Search terms' },
  { key: 'api_string', label: 'API URL' },
  { key: 'api_key', label: 'API parameters' },
  { key: 'api_data_type', label: 'API data type' },
  { key: 'return_value', label: 'API return value path' },
  { key: 'api_logo', label: 'API logo URL' },
];

function toDraft(d) {
  return {
    title: d.title || '',
    body: d.body || '',
    status: d.status,
    app_only: !!d.app_only,
    focus: d.focus || '',
    game_type: d.game_type || '',
    tag_ids: [...(d.tag_ids || [])].sort((a, b) => a - b),
    prompt_one: (d.onboard && d.onboard.prompt_one) || '',
    prompt_two: (d.onboard && d.onboard.prompt_two) || '',
    picture_one_id: (d.onboard && d.onboard.picture_one_id) || null,
    picture_two_id: (d.onboard && d.onboard.picture_two_id) || null,
    prompts: d.prompts || '',
    level: d.level == null ? '' : String(d.level),
    search_terms: d.search_terms || '',
    api_string: d.api_string || '',
    api_key: d.api_key || '',
    api_data_type: d.api_data_type || '',
    return_value: d.return_value || '',
    api_logo: d.api_logo || '',
    api_post_call: !!d.api_post_call,
  };
}

function toPayload(draft, pictures) {
  const { prompt_one, prompt_two, picture_one_id, picture_two_id, ...rest } = draft;
  const [one, two] = effectivePracticePictures(pictures, picture_one_id, picture_two_id);
  return {
    ...rest,
    onboard: {
      prompt_one,
      prompt_two,
      picture_one_id: one ? one.id : null,
      picture_two_id: two ? two.id : null,
    },
  };
}

function Card({ title, subtitle, children, className = '' }) {
  return (
    <section className={`aaCard ${className}`}>
      <header className="aaCard__header">
        <h3 className="aaCard__title">{title}</h3>
        {subtitle && <p className="aaCard__subtitle">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="aaField">
      <span className="aaField__label">{label}</span>
      {children}
      {hint && <span className="aaField__hint">{hint}</span>}
    </label>
  );
}

export default function ActivityEditor({
  api, detail, tags, gameTagId, gameTypes, justSaved, onClose, onDetailChange, onDeleted, onDirtyChange,
}) {
  const [baseline] = useState(() => toDraft(detail));
  const [draft, setDraft] = useState(baseline);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(!!justSaved);
  const [teaserBusy, setTeaserBusy] = useState(false);
  const [teaserPickId, setTeaserPickId] = useState(null);
  const [teaserError, setTeaserError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(baseline), [draft, baseline]);
  useEffect(() => { onDirtyChange(dirty); }, [dirty]);
  useEffect(() => () => onDirtyChange(false), []);

  useEffect(() => {
    if (!savedFlash) return undefined;
    const t = setTimeout(() => setSavedFlash(false), 2500);
    return () => clearTimeout(t);
  }, [savedFlash]);

  const set = (key) => (e) => {
    const value = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setDraft((d) => ({ ...d, [key]: value }));
  };

  function toggleTag(id) {
    setDraft((d) => {
      const has = d.tag_ids.includes(id);
      const next = has ? d.tag_ids.filter((t) => t !== id) : [...d.tag_ids, id];
      return { ...d, tag_ids: next.sort((a, b) => a - b) };
    });
  }

  async function save() {
    if (!draft.title.trim()) {
      setSaveError('The title can’t be empty.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await api.update(detail.id, toPayload(draft, detail.pictures));
      onDirtyChange(false);
      onDetailChange(updated, { resetDraft: true });
    } catch (e) {
      setSaveError(e.message);
      setSaving(false);
    }
  }

  async function uploadTeaser(file) {
    if (!file) return;
    setTeaserBusy(true);
    setTeaserError(null);
    try {
      onDetailChange(await api.uploadTeaser(detail.id, file));
    } catch (e) {
      setTeaserError(e.message);
    } finally {
      setTeaserBusy(false);
    }
  }

  async function pickTeaser(picture) {
    setTeaserBusy(true);
    setTeaserPickId(picture.id);
    setTeaserError(null);
    try {
      onDetailChange(await api.teaserFromPicture(detail.id, picture.id));
    } catch (e) {
      setTeaserError(e.message);
    } finally {
      setTeaserBusy(false);
      setTeaserPickId(null);
    }
  }

  async function removeTeaser() {
    if (!window.confirm('Remove the teaser image?')) return;
    setTeaserBusy(true);
    setTeaserError(null);
    try {
      onDetailChange(await api.deleteTeaser(detail.id));
    } catch (e) {
      setTeaserError(e.message);
    } finally {
      setTeaserBusy(false);
    }
  }

  async function destroy() {
    if (!window.confirm(`Delete “${detail.title}” permanently? This also deletes its pictures and practice prompts.`)) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.destroy(detail.id);
      onDeleted(detail.id);
    } catch (e) {
      setDeleteError(e.message);
      setDeleting(false);
    }
  }

  const readiness = gameReadiness({
    status: draft.status,
    gameType: draft.game_type,
    gameTypes,
    tagIds: draft.tag_ids,
    gameTagId,
    pictureCount: detail.pictures.length,
    boardSize: detail.board_size,
    minPictures: detail.min_game_pictures,
    practice: !!(draft.prompt_one.trim() || draft.prompt_two.trim()),
  });
  const selectedGameType = gameTypes.find((t) => t.key === draft.game_type);
  const isGame = !!draft.game_type || draft.tag_ids.includes(gameTagId);
  const showPictures = draft.game_type === 'matching_grid' || detail.pictures.length > 0;
  const usage = detail.usage;
  const practicePictures = effectivePracticePictures(detail.pictures, draft.picture_one_id, draft.picture_two_id);

  function assignPractice(slot, pictureId) {
    setDraft((current) => {
      if (current[slot] === pictureId) return current;
      const other = slot === 'picture_one_id' ? 'picture_two_id' : 'picture_one_id';
      const next = { ...current, [slot]: pictureId };
      if (next[other] === pictureId) next[other] = null;
      return next;
    });
  }

  return (
    <div className="aaEditor">
      <div className="aaEditor__head">
        <CoverTile activity={detail} size={56} />
        <div className="aaEditor__headText">
          <h2 className="aaEditor__title">{draft.title || 'Untitled'}</h2>
          <p className="aaEditor__meta">
            #{detail.id}
            {detail.author && <> · by {detail.author.name || `user ${detail.author.id}`}</>}
            {detail.updated_at && <> · updated {new Date(detail.updated_at).toLocaleDateString()}</>}
          </p>
        </div>
        <button type="button" className="aaIconButton" onClick={onClose} aria-label="Close editor">✕</button>
      </div>

      <div className="aaSegment" role="radiogroup" aria-label="Status">
        {Object.keys(STATUS_META).map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={draft.status === key}
            className={`aaSegment__option aaSegment__option--${key}${draft.status === key ? ' aaSegment__option--active' : ''}`}
            onClick={() => set('status')(key)}
          >
            {STATUS_META[key].label}
          </button>
        ))}
      </div>
      <p className="aaSegment__hint">{STATUS_META[draft.status].hint}</p>

      <Card title="Details" subtitle="What users see on the activity card.">
        <Field label="Title">
          <input className="aaInput" value={draft.title} onChange={set('title')} maxLength={255} />
        </Field>
        <Field label="Directions" hint="The description shown on the activity card and in the game picker.">
          <textarea className="aaInput aaTextarea" rows={6} value={draft.body} onChange={set('body')} />
        </Field>
        <Field label="Vocabulary focus" hint="Optional. Shown as a label on mobile activity cards, e.g. “Prepositions”.">
          <input className="aaInput" value={draft.focus} onChange={set('focus')} maxLength={255} />
        </Field>
        <div className="aaField">
          <span className="aaField__label">Tags</span>
          <div className="aaTagPicker">
            {tags.map((t) => {
              const on = draft.tag_ids.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`aaTagToggle${on ? ' aaTagToggle--on' : ''}`}
                  style={on ? { background: t.color, borderColor: t.color } : { borderColor: t.color, color: t.color }}
                  aria-pressed={on}
                  onClick={() => toggleTag(t.id)}
                >
                  {t.id === gameTagId ? '🎮 ' : ''}{t.name}
                </button>
              );
            })}
          </div>
        </div>
        <label className="aaCheck aaCheck--block">
          <input type="checkbox" checked={draft.app_only} onChange={set('app_only')} />
          <span>
            Mobile app only
            <span className="aaField__hint">
              On the website, shows “available in the mobile app” instead of a start button. Game packs ignore this, since they can be played on web.
            </span>
          </span>
        </label>
      </Card>

      <Card
        title="🎮 Game"
        subtitle={isGame ? 'Which game this activity plays, and its content.' : 'Choose a game type to make this activity playable.'}
        className={isGame ? '' : 'aaCard--muted'}
      >
        <Field
          label="Game type"
          hint={selectedGameType ? selectedGameType.description : 'Not a game. It only appears on Activities.'}
        >
          <select className="aaInput" value={draft.game_type} onChange={set('game_type')}>
            <option value="">Not a game</option>
            {gameTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </Field>
        {isGame && (
          <ul className="aaChecklist">
            {readiness.checks.map((c) => (
              <li key={c.key} className={`aaChecklist__item aaChecklist__item--${c.state}`}>
                <span aria-hidden="true">{c.state === 'ok' ? '✅' : c.state === 'warn' ? '⚠️' : '⭕'}</span>
                {c.text}
              </li>
            ))}
          </ul>
        )}
        {readiness.testable && (
          <div className="aaTryGame">
            <a className="aaButton aaButton--soft aaButton--small" href={`/games?pack=${detail.id}`} target="_blank" rel="noreferrer">
              Try it in the game ↗
            </a>
            <span className="aaField__hint">
              {readiness.playable ? 'Players can pick this from the game picker.' : 'Works for testing even before it’s Live. Players won’t see it in the picker yet.'}
              {dirty && ' Save first to test your latest changes.'}
            </span>
          </div>
        )}

        {showPictures && (
          <>
            <PicturesSection
              api={api}
              detail={detail}
              practicePictureIds={practicePictures.map((picture) => (picture ? picture.id : null))}
              onDetailChange={onDetailChange}
            />

            <div className="aaPractice">
              <h4 className="aaSubheading">Practice prompts</h4>
              <p className="aaField__hint">
                Each prompt is what the describer reads for one picture. Pick that picture under the prompt, then save. If you don’t pick one, practice uses pictures 2 and 3.
              </p>
              {[['prompt_one', 'picture_one_id', 'Practice round 1'], ['prompt_two', 'picture_two_id', 'Practice round 2']].map(([promptKey, pictureKey, label], i) => {
                const pic = practicePictures[i];
                const picIndex = pic ? detail.pictures.findIndex((p) => p.id === pic.id) : -1;
                return (
                  <div key={promptKey}>
                    <div className="aaPractice__row">
                      {pic ? (
                        <img className="aaPractice__thumb" src={pic.url} alt="" />
                      ) : (
                        <span className="aaPractice__thumb aaPractice__thumb--empty">?</span>
                      )}
                      <div className="aaPractice__main">
                        <Field label={picIndex >= 0 ? `${label}: picture ${picIndex + 1}` : label}>
                          <textarea className="aaInput aaTextarea" rows={2} value={draft[promptKey]} onChange={set(promptKey)} />
                        </Field>
                      </div>
                    </div>
                    {detail.pictures.some((p) => !p.premium) && (
                      <div className="aaTeaserPick aaPractice__pick">
                        <span className="aaField__label">Picture for this prompt</span>
                        <div className="aaTeaserPick__grid">
                          {detail.pictures.map((p, n) => {
                            if (p.premium) return null;
                            const current = !!pic && p.id === pic.id;
                            return (
                              <button
                                key={p.id}
                                type="button"
                                className={`aaTeaserPick__item${current ? ' aaTeaserPick__item--current' : ''}`}
                                onClick={() => assignPractice(pictureKey, p.id)}
                                disabled={current}
                                aria-pressed={current}
                                aria-label={current
                                  ? `Picture ${n + 1} is ${label.toLowerCase()}`
                                  : `Use picture ${n + 1} for ${label.toLowerCase()}`}
                              >
                                <img src={p.url} alt="" loading="lazy" />
                                <span className="aaTeaserPick__number">{n + 1}</span>
                                {current && <span className="aaTeaserPick__check" aria-hidden="true">✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>

      <Card title="Teaser image" subtitle="Animated GIF or image shown on activity cards in the mobile app.">
        <div className="aaTeaser">
          {detail.teaser_gif_url ? (
            <img className="aaTeaser__img" src={detail.teaser_gif_url} alt="Teaser" />
          ) : (
            <span className="aaTeaser__img aaTeaser__img--empty">No teaser</span>
          )}
          <div className="aaTeaser__actions">
            <label className={`aaButton aaButton--small${teaserBusy ? ' aaButton--disabled' : ''}`}>
              {teaserBusy ? 'Working…' : detail.teaser_gif_url ? 'Replace' : 'Upload'}
              <input
                type="file"
                accept="image/gif,image/png,image/jpeg"
                hidden
                disabled={teaserBusy}
                onChange={(e) => { uploadTeaser(e.target.files[0]); e.target.value = ''; }}
              />
            </label>
            {detail.teaser_gif_url && (
              <button type="button" className="aaButton aaButton--ghost aaButton--small" onClick={removeTeaser} disabled={teaserBusy}>
                Remove
              </button>
            )}
          </div>
        </div>
        {detail.pictures.length > 0 && (
          <div className="aaTeaserPick">
            <span className="aaField__label">Or use one of this activity’s pictures</span>
            <div className="aaTeaserPick__grid">
              {detail.pictures.map((p, i) => {
                const current = !!detail.teaser_file_name && p.file_name === detail.teaser_file_name;
                const busy = teaserPickId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`aaTeaserPick__item${current ? ' aaTeaserPick__item--current' : ''}${busy ? ' aaTeaserPick__item--busy' : ''}`}
                    onClick={() => pickTeaser(p)}
                    disabled={teaserBusy || current}
                    aria-pressed={current}
                    aria-label={current ? `Picture ${i + 1} is the teaser` : `Use picture ${i + 1} as the teaser`}
                    title={current ? 'Current teaser' : 'Use as teaser'}
                  >
                    <img src={p.url} alt="" loading="lazy" />
                    <span className="aaTeaserPick__number">{busy ? '⏳' : i + 1}</span>
                    {current && <span className="aaTeaserPick__check" aria-hidden="true">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {teaserError && <p className="aaError">{teaserError}</p>}
      </Card>

      <details className="aaCard aaLegacy">
        <summary className="aaCard__title">Legacy fields</summary>
        <p className="aaCard__subtitle">From the old API games. The current apps don’t use these.</p>
        {LEGACY_FIELDS.map((f) => (
          <Field key={f.key} label={f.label}>
            {f.multiline ? (
              <textarea className="aaInput aaTextarea" rows={3} value={draft[f.key]} onChange={set(f.key)} />
            ) : (
              <input className="aaInput" type={f.type || 'text'} value={draft[f.key]} onChange={set(f.key)} />
            )}
          </Field>
        ))}
        <label className="aaCheck aaCheck--block">
          <input type="checkbox" checked={draft.api_post_call} onChange={set('api_post_call')} />
          API uses POST
        </label>
      </details>

      <Card title="Usage" className="aaCard--danger">
        <ul className="aaUsage">
          <li><strong>{usage.games}</strong> games played{usage.active_games > 0 && <> ({usage.active_games} in progress)</>}</li>
          <li><strong>{usage.legacy_games}</strong> old matching games</li>
          <li><strong>{usage.meetings}</strong> meetings</li>
          <li><strong>{usage.favorites}</strong> favorites</li>
        </ul>
        {usage.can_delete ? (
          <button type="button" className="aaButton aaButton--danger aaButton--small" onClick={destroy} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete activity'}
          </button>
        ) : (
          <p className="aaField__hint">
            This activity has play or meeting history, so it can’t be deleted. Set it to <strong>Hidden</strong> to remove it from users.
          </p>
        )}
        {deleteError && <p className="aaError">{deleteError}</p>}
      </Card>

      {(dirty || saving || saveError || savedFlash) && (
        <div className="aaSaveBar">
          {saveError ? (
            <span className="aaError">{saveError}</span>
          ) : (
            <span>{dirty ? 'Unsaved changes' : '✅ Saved'}</span>
          )}
          <div className="aaSaveBar__actions">
            {dirty && (
              <button type="button" className="aaButton aaButton--ghost" onClick={() => { setDraft(baseline); setSaveError(null); }} disabled={saving}>
                Discard
              </button>
            )}
            <button type="button" className="aaButton aaButton--primary" onClick={save} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
